import { useUpcValidator } from '~/modules/upc/composables/useUpcValidator';
import { computed, type ComputedRef } from 'vue';
import useGWPStore from '~/stores/useGWPStore';
import { toValue } from '@vueuse/core';
import { Cookie } from '@vue-util/cookie';
import { cookies, fulfillmentErrors } from 'config';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useUpc from '~/modules/upc/composables/useUpc';
import { UPC_VALIDATION_MESSAGES } from '~/modules/upc/consts/validation';
import useSelectedPlan from '~/modules/worry-no-more/composables/useSelectedPlan';
import { useBrand } from '~/composables/useBrand';
import { useBigTicketATBValidator } from '~/modules/availability/composables/useBigTicketATBValidator';
import { useBigTicketDeliveryMethods } from '~/modules/availability/composables/useBigTicketDeliveryMethods';
import { useFulfilmentData } from '~/modules/availability/composables/useFulfilmentData';
import { useAvailability } from '~/modules/availability/composables/useAvailability';
import generateSameDayMessage from '@nuxt-common/fulfillment/app/utils/generateSameDayMessage';
import useGetItTodayExperiments from '~/modules/availability/composables/useGetItTodayExperiments';
import { useMarketPlace } from '~/composables/useMarketPlace';
import { useQuantity } from '~/modules/product/composables/useQuantity';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { useATBUTag } from './useATBUTag/useATBUTag';
import { useBagContext } from './useBagContext';

type AtbPayload = {
  data: { item: Record<string, unknown> };
  options: {
    sequenceNumber?: string;
    bagGuid: string | undefined;
    sourceUtagData: Record<string, unknown>;
  };
  flags: { isMcom: boolean };
};

export type UseATBPayload = {
  payload: ComputedRef<AtbPayload>;
  payloadValidator: () => string | null;
};

/** @typedef {import('../types').ATBPayloadItem} ATBPayloadItem */
/** @typedef {import('../types').ATBSourceUtagData} ATBSourceUtagData */
/** @typedef {import('../types').ATBPayload} ATBPayload */
/** @typedef {import('../types').ATBPayloadValidator} ATBPayloadValidator */

/**
 * * Prepare and validate the payload for the ATB API request.
 *
 * @description This composable provides a way to build the payload for the ATB API request and validate it.
 * It is used in the ATB composable (DI-like pattern).
 * It allows excluding the payload building and validation logic from the ATB composable.
 * It helps to keep the ATB composable clean and simple and let it focus on the ATB functionality.
 * As well it should help move ATB composable to the separate package in the future.
 * That way we can easily mock the payload and the payload validator in the ATB composable tests.
 * Also, it is easier to test the payload validator separately.
 *
 * @example
 * ```js
 * import { useATBPayload } from '~/composables/useATBPayload';
 * import { useAddToBag } from '~/modules/add-to-bag/composables/useAddToBag';
 * const { payload, payloadValidator } = useATBPayload();
 *
 * const { ... } = useAddToBag(payload, { payloadValidator });
 * ```
 * @param {Object} [options]
 * @param {import('@vueuse/shared').MaybeRefOrGetter<Partial<ATBPayloadItem>>} [options.additionalPayload]
 * @param {import('@vueuse/shared').MaybeRefOrGetter<ATBSourceUtagData|undefined>} [options.sourceUtagData]
 */
export const useATBPayload = ({ additionalPayload, sourceUtagData } = {}): UseATBPayload => {
  const store = useGWPStore();
  const { quantity } = useQuantity();
  const { isMcom } = useBrand();
  const bagContext = useBagContext();

  const selectedPrice = useProductPrice();
  const { selectedUpcId, selectedUpcNumber } = useUpc();
  const { bigTicketATBValidator } = useBigTicketATBValidator();
  const { upcValidator } = useUpcValidator();
  const sourceUtagDataMain = useATBUTag();
  const { storeLocation, selectedRadioButton, isDataLoaded, fulfillmentError, zipCode } = useFulfilmentData();
  const { productInfo } = useAvailability();
  const { isProtectionPlanEligibleFlag, isMarketPlaceEligibleFlag } = useProductFlags();
  const { includeDeliveryMethodInATBPayload } = useBigTicketDeliveryMethods();

  const { sellerId } = useMarketPlace();
  const { includePlanInATBPayload } = useSelectedPlan();
  const { isChipFulfillmentEnabled } = useGetItTodayExperiments();

  /**
   * @type {import('vue').ComputedRef<ATBPayload>}
   */
  const payload = computed(() => {
    const upcId = selectedUpcId.value?.toString();
    const upcNumber = selectedUpcNumber.value?.toString();
    const registryId = 0; // TODO
    const source = 'PDPA2B';
    const loyaltyPoints = true;
    const gwpPayload = {};

    // prodSelectionInfo      // TODO
    let pickupPayload = {
      pickUpFromStore: 'false',
    };

    if (selectedRadioButton.value === 'Pickup') {
      pickupPayload = {
        ...pickupPayload,
        pickUpFromStore: 'true',
        deliveryOption: 'pickInStore',
        storeLocation: storeLocation.value?.toString(),
      };
    }

    if (selectedPrice.value?.isGWPAvailable) {
      const preparedGwpPayload = store.getPreparedGwpPayload(selectedPrice.value.lowestSalePriceTier);
      if (preparedGwpPayload.length) {
        gwpPayload.gwpItemSelections = preparedGwpPayload;
      }
    }

    return {
      data: {
        item: {
          /**
           * For some reason, upcId and upcNumber shoud be strings for Gift Cards pages.
           * It works fine with numbers for other pages.
           * I've checked if it's possible always to use strings for upcId and upcNumber.
           * And it works fine for all pages (28 March 2024).
           */
          upcId,
          upcNumber,
          registryId,
          source,
          loyaltyPoints,
          quantity: quantity.value,
          ...gwpPayload,
          ...pickupPayload,
          ...(additionalPayload ? toValue(additionalPayload) : {}),
          ...includePlanInATBPayload(),
          ...includeDeliveryMethodInATBPayload(),
          pdpAtbProtectionPlanEligible: isProtectionPlanEligibleFlag,
          isMarketPlaceEligibleFlag, // TODO: Does XAPI need this flag?
          sellerId: sellerId.value,
          ...(isChipFulfillmentEnabled.value && { sameDayDeliverySelected: selectedRadioButton.value === 'SameDay' }),
        },
      },
      options: {
        ...(bagContext.isUpdateBagEnabled ? { sequenceNumber: bagContext.sequenceNumber } : {}),
        sourceUtagData: {
          ...sourceUtagDataMain.value,
          ...(sourceUtagData ? toValue(sourceUtagData) : {}),
        },
        bagGuid: Cookie.get(cookies.bagGuid),
      },
      flags: {
        isMcom,
      },
    };
  });

  const getFulfillmentValidationError = (): string | null => {
    if (!isChipFulfillmentEnabled.value || !isDataLoaded.value) {
      return null;
    }

    const type = selectedRadioButton.value;
    const info = productInfo.value;

    if (type === 'SameDay') {
      const isSddAvailable = info?.sameDay?.available;
      if (isSddAvailable === false) {
        const sameDayInfo = info?.sameDay;
        const shipFromStore = info?.shipFromStore;
        const sameDayMsg = sameDayInfo && shipFromStore ? generateSameDayMessage(sameDayInfo, shipFromStore, zipCode?.value) : null;
        const isNextDay = sameDayMsg?.deliveryTomorrow;
        return isNextDay ? fulfillmentErrors.NextDay : fulfillmentErrors.SameDay;
      }
    }

    if (type === 'Pickup') {
      const isBopsAvailable = info?.pickup?.bopsAvailability;
      const isBossAvailable = info?.pickup?.bossAvailability;
      if (isBopsAvailable === false && isBossAvailable === false) {
        return fulfillmentErrors.Pickup;
      }
    }

    if (type === 'ShipIt') {
      const isShipAvailable = info?.ship?.available;
      if (isShipAvailable === false) {
        return fulfillmentErrors.ShipIt;
      }
    }

    return null;
  };

  /**
   * @type {ATBPayloadValidator}
   */
  const payloadValidator = () => {
    const btValidationMessage = bigTicketATBValidator();
    if (btValidationMessage) return btValidationMessage;
    if (isMarketPlaceEligibleFlag && (!sellerId || Number(sellerId) === -1)) {
      return UPC_VALIDATION_MESSAGES.SIZE_COLOR_NOT_AVAILABLE;
    }
    const upcValMessage = upcValidator();
    if (upcValMessage) return upcValMessage;

    const fulfillmentErrorMsg = getFulfillmentValidationError();
    if (fulfillmentError) {
      fulfillmentError.value = fulfillmentErrorMsg;
    }
    return fulfillmentErrorMsg;
  };

  return {
    payload,
    payloadValidator,
  };
};
